<template>
  <b-card no-body>
    <div class="m-2">
      <b-row>
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <label>Mostrar</label>
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector d-inline-block ml-50 mr-1"
          />
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="openCreateModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-25"
            />
            <span class="align-middle">Nueva ruta</span>
          </b-button>
        </b-col>

        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input
              v-model="searchQuery"
              class="d-inline-block mr-1"
              placeholder="Buscar..."
            />
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="filterUserId"
            :options="vendors"
            label="name"
            :reduce="u => u.id"
            placeholder="Filtrar por vendedor"
            :clearable="true"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
        >
          <v-select
            v-model="filterStatus"
            :options="statusOptions"
            :reduce="opt => opt.value"
            placeholder="Filtrar por estado"
            :clearable="true"
          />
        </b-col>
      </b-row>
    </div>

    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          v-if="routesMeta"
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted">
            Mostrando del {{ routesMeta.from }} al {{ routesMeta.to }} de {{ routesMeta.total }} registros
          </span>
        </b-col>
      </b-row>
    </div>

    <b-table
      class="position-relative"
      hover
      responsive
      small
      :busy="tableIsBusy"
      primary-key="id"
      show-empty
      empty-text="No se encontraron rutas"
      :items="routesData"
      :fields="routesFields"
      :per-page="perPage"
    >
      <template #table-busy>
        <div class="text-center text-dark my-2">
          <b-spinner class="align-middle" />
        </div>
      </template>

      <template #head(id)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}</span>
      </template>

      <template #head(name)="data">
        <span
          class="text-dark pointer"
          @click="sortColumn(data.column)"
        >{{ data.label }}</span>
      </template>

      <template #cell(id)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="openEditModal(data.item)"
        >
          {{ data.item.id }}
        </b-link>
      </template>

      <template #cell(name)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="openEditModal(data.item)"
        >
          {{ data.item.name }}
        </b-link>
        <small
          v-if="data.item.description"
          class="d-block text-muted"
        >
          {{ data.item.description }}
        </small>
      </template>

      <template #cell(user)="data">
        <b-badge
          v-if="data.item.user && data.item.user.name"
          variant="secondary"
        >
          {{ data.item.user.name }}
        </b-badge>
        <small
          v-else
          class="text-muted"
        >
          Sin vendedor
        </small>
      </template>

      <template #cell(customers_count)="data">
        <b-link @click="openCustomersModal(data.item)">
          <b-badge variant="light-info">
            {{ data.item.customers_count || 0 }}
          </b-badge>
        </b-link>
      </template>

      <template #cell(status)="data">
        <b-badge :variant="`light-${ data.item.status === 'Y' ? 'success' : 'danger' }`">
          {{ data.item.status === 'Y' ? 'Activo' : 'Inactivo' }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-button
          variant="flat-info"
          size="sm"
          class="p-50"
          title="Ver clientes"
          @click="openCustomersModal(data.item)"
        >
          <feather-icon
            icon="UsersIcon"
            size="14"
          />
        </b-button>
        <b-button
          variant="flat-primary"
          size="sm"
          class="p-50"
          title="Editar"
          @click="openEditModal(data.item)"
        >
          <feather-icon
            icon="EditIcon"
            size="14"
          />
        </b-button>
        <b-button
          variant="flat-danger"
          size="sm"
          class="p-50"
          title="Eliminar"
          @click="deleteRoute(data.item)"
        >
          <feather-icon
            icon="TrashIcon"
            size="14"
          />
        </b-button>
      </template>
    </b-table>

    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-if="routesMeta"
            v-model="currentPage"
            :total-rows="routesMeta.total"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            prev-class="prev-item"
            next-class="next-item"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <customer-route-form-modal
      :show.sync="modalVisible"
      :route="routeBeingEdited"
      @saved="onRouteSaved"
    />

    <customer-route-customers-modal
      :show.sync="customersModalVisible"
      :route="routeForCustomers"
      @changed="getRoutes"
    />
  </b-card>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import CustomerRouteFormModal from '../customers/CustomerRouteFormModal.vue'
import CustomerRouteCustomersModal from './CustomerRouteCustomersModal.vue'

export default {
  name: 'CustomerRoutes',

  components: {
    vSelect,
    CustomerRouteFormModal,
    CustomerRouteCustomersModal,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      searchQuery: '',
      sortField: 'id',
      sortDesc: 'desc',
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      currentPage: 1,
      searchQueryTimeout: null,
      filterUserId: null,
      filterStatus: null,

      vendors: [],

      modalVisible: false,
      routeBeingEdited: null,

      customersModalVisible: false,
      routeForCustomers: null,

      routesFields: [
        { key: 'id', label: '#' },
        { key: 'name', label: 'Ruta' },
        { key: 'user', label: 'Vendedor asignado' },
        { key: 'customers_count', label: 'Clientes' },
        { key: 'status', label: 'Estado' },
        { key: 'created_at', label: 'Creado' },
        { key: 'actions', label: 'Acciones' },
      ],
      routesData: null,
      routesMeta: null,
      tableIsBusy: true,

      statusOptions: [
        { value: 'Y', label: 'Activo' },
        { value: 'N', label: 'Inactivo' },
      ],
    }
  },

  watch: {
    perPage() {
      this.tableIsBusy = true
      this.getRoutes()
    },
    currentPage() {
      this.tableIsBusy = true
      this.getRoutes()
    },
    filterUserId() {
      this.currentPage = 1
      this.tableIsBusy = true
      this.getRoutes()
    },
    filterStatus() {
      this.currentPage = 1
      this.tableIsBusy = true
      this.getRoutes()
    },
    searchQuery() {
      clearTimeout(this.searchQueryTimeout)
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.currentPage = 1
        this.tableIsBusy = true
        this.getRoutes()
      } else {
        this.searchQueryTimeout = setTimeout(() => {
          this.currentPage = 1
          this.tableIsBusy = true
          this.getRoutes()
        }, 300)
      }
    },
  },

  created() {
    this.loadVendors()
    this.getRoutes()
  },

  methods: {
    loadVendors() {
      const filterArray = [{ field: 'active', value: 'Y' }]
      axios
        .get(`system/users?filters=${JSON.stringify(filterArray)}&perPage=9999`)
        .then(response => {
          this.vendors = response.data.data || []
        })
        .catch(error => {
          this.showErrors(error)
        })
    },

    getRoutes() {
      const params = {
        perPage: this.perPage,
        page: this.currentPage,
        query: this.searchQuery,
        sortField: this.sortField,
        sortDesc: this.sortDesc,
      }
      if (this.filterUserId) {
        params.filterField = 'user_id'
        params.filterValue = this.filterUserId
      } else if (this.filterStatus) {
        params.filterField = 'status'
        params.filterValue = this.filterStatus
      }

      axios
        .get('customer-route', { params })
        .then(response => {
          this.tableIsBusy = false
          this.routesMeta = response.data.meta
          this.routesData = response.data.data
        })
        .catch(error => {
          this.tableIsBusy = false
          this.showErrors(error)
        })
    },

    openCreateModal() {
      this.routeBeingEdited = null
      this.modalVisible = true
    },

    openEditModal(route) {
      this.routeBeingEdited = route
      this.modalVisible = true
    },

    openCustomersModal(route) {
      this.routeForCustomers = route
      this.customersModalVisible = true
    },

    onRouteSaved() {
      this.tableIsBusy = true
      this.getRoutes()
    },

    deleteRoute(route) {
      const customersCount = route.customers_count || 0
      const warning = customersCount > 0
        ? `Esta ruta tiene ${customersCount} cliente(s) asignado(s). Al eliminar, esos clientes quedarán sin ruta. ¿Continuar?`
        : '¿Deseas eliminar esta ruta?'

      this.$bvModal
        .msgBoxConfirm(warning, {
          title: `Eliminar ${route.name}`,
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'SI',
          cancelTitle: 'NO',
          okTitleVariant: 'outline-danger',
          centered: true,
        })
        .then(value => {
          if (value) {
            axios
              .delete(`customer-route/${route.id}`)
              .then(() => {
                this.makeToast('success', 'Ruta eliminada', `Se eliminó la ruta ${route.name}.`)
                this.getRoutes()
              })
              .catch(error => {
                this.showErrors(error)
              })
          }
        })
    },

    sortColumn(field) {
      if (this.sortField === field) {
        this.sortDesc = this.sortDesc === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDesc = 'asc'
      }
      this.getRoutes()
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, { title, variant, solid: true })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
.pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
